<template>
  <v-dialog
    v-model="show"
    max-width="90vw"
    scrollable
  >
    <v-card
      max-height="80vh"
    >
      <v-card-title>
        <v-row>
          <v-col cols="auto">
            <v-btn
              color="primary"
              small
              :outlined="!activeBtns.lastMonth"
              :elevation="activeBtns.lastMonth ? 4 : 1"
              @click="getReports('lastMonth')"
            >
              Last Month
            </v-btn>
          </v-col>
          <v-col cols="auto">
            <v-btn
              color="primary"
              small
              :outlined="!activeBtns.lastWeek"
              :elevation="activeBtns.lastWeek ? 4 : 1"
              @click="getReports('lastWeek')"
            >
              Last Week
            </v-btn>
          </v-col>
          <v-col cols="auto">
            <v-btn
              color="primary"
              small
              :outlined="!activeBtns.today"
              :elevation="activeBtns.today ? 4 : 1"
              @click="getReports('today')"
            >
              Today
            </v-btn>
          </v-col>
          <v-col cols="auto">
            <v-btn
              color="primary"
              small
              :outlined="!activeBtns.week"
              :elevation="activeBtns.week ? 4 : 1"
              @click="getReports('week')"
            >
              This week
            </v-btn>
          </v-col>
          <v-col cols="auto">
            <v-btn
              color="primary"
              small
              :outlined="!activeBtns.month"
              :elevation="activeBtns.month ? 4 : 1"
              @click="getReports('month')"
            >
              This month
            </v-btn>
          </v-col>
          <v-col cols="auto">
            <RangeDatePickerMenu
              v-model="dateRange"
              showButtons
              @onOkClick="getReports()"
            />
          </v-col>
          <v-col cols="auto">
            <v-select
              dense
              hide-details
              label="Group By"
              clearable
              v-model="groupBy"
              :items="groupByItems"
              item-text="text"
              item-value="value"
              @change="generateReportsList"
            ></v-select>
          </v-col>
          <v-spacer></v-spacer>
          <v-btn
            icon
            blur
            @click.prevent="show = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-row>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text>
        <div
          class="py-2 var(--v-theme-on-surface) lighten-4 text-center"
          v-if="reportsList.length === 0"
        >
          {{ loading ? 'Fetching...' : 'No Data' }}
        </div>
        <div v-else>
          <div class="text-right" v-if="groupBy">
            Reported / Paid
          </div>
          <div
            class="group"
            v-for="(group, groupKey) in reportsList"
            :key="groupKey"
          >
            <div
              class="d-flex group__title primary white--text pa-1"
              v-if="group.group"
              @click="group.isOpen = !group.isOpen"
            >
              <div>
                <v-icon color="white">
                  {{ group.isOpen ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                </v-icon>
                {{ group.group }}
              </div>
              <v-spacer></v-spacer>
              <div>
                {{ calculateTotalOf(group.items, 'Real_Dur') }} / {{ calculateTotalOf(group.items, 'Report_Dur') }}
              </div>
            </div>
            <div class="group_data" v-if="group.isOpen">
              <table class="data-table">
                <thead>
                  <tr>
                    <th
                      :class="{
                        'sorted-by': group.sortBy === thead.value
                      }"
                      :style="{
                        'min-width': thead.width + 'px'
                      }"
                      v-for="(thead, theadKey) in colDefs" :key="theadKey"
                      @click="sortGroup(group, thead.value)"
                    >
                      <span>
                        {{ thead.text }}
                        <div v-if="thead.showTotal">
                          Total: {{ calculateTotalOf(group.items, thead.value) }}
                        </div>
                      </span>
                      <span v-if="group.sortBy === thead.value">
                        <v-icon small>
                          {{ group.sortWay === 'asc' ? 'mdi-arrow-down' : 'mdi-arrow-up' }}
                        </v-icon>
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(iRow, iRowKey) in group.items" :key="iRowKey">
                    <td
                      v-for="(iCell, iCellKey) in colDefs" :key="iCellKey"
                      :title="iRow[iCell.value]"
                    >
                      <span>{{ iRow[iCell.value] }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <table>
          <tr>
            <td class="px-1">Total Reported:</td>
            <th class="px-1">{{ calculateTotalOf(reportsListRaw, 'Real_Dur') }}</th>
          </tr>
          <tr>
            <td class="px-1">Total Paid:</td>
            <th class="px-1">{{ calculateTotalOf(reportsListRaw, 'Report_Dur') }}</th>
          </tr>
        </table>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import moment from 'moment'

export default {
  name: 'TotalDetailsDialog',
  props: {
    value: {
      required: true,
      type: Boolean,
      default: false
    },
    rangeFor: {
      required: true,
      default: 'today'
    }
  },
  components: {
    RangeDatePickerMenu: () => import('@/components/global/RangeDatePickerMenu.vue')
  },
  data () {
    return {
      loading: false,
      dialog: true,
      activeBtns: {
        lastMonth: false,
        lastWeek: false,
        today: false,
        week: false,
        month: false
      },
      reportsListRaw: [],
      reportsList: [],
      dates: [],
      groupBy: null,
      groupByItems: [
        {
          text: 'Client',
          value: 'Client_Name'
        },
        {
          text: 'Project',
          value: 'Project_Name'
        },
        {
          text: 'Start Date',
          value: 'Start_Date'
        },
        {
          text: 'Activity',
          value: 'Activity_Type'
        }
      ],
      colDefs: [
        {
          text: 'Client',
          value: 'Client_Name',
          width: 250
        },
        {
          text: 'Project name',
          value: 'Project_Name',
          width: 250
        },
        { text: 'Task name', value: 'Task_Name', width: 300 },
        { text: 'Est./Rep.', value: 'EstRep', width: 120 },
        { text: 'Start date', value: 'Start_Date', width: 150 },
        { text: 'Reported duration', value: 'Real_Dur', width: 150, showTotal: true },
        { text: 'Paid duration', value: 'Report_Dur', width: 150, showTotal: true },
        { text: 'Activity', value: 'Activity_Type', width: 200 },
        { text: 'Description', value: 'Description', width: 200 }
      ]
    }
  },
  computed: {
    show: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    },
    user () {
      return this.$store.getters.getUser
    },
    dateRange: {
      get () {
        return this.dates
      },
      set (val) {
        if (val === 'lastMonth') {
          this.dates = [
            moment().subtract(1, 'months').startOf('month').format('YYYY-MM-DD'),
            moment().subtract(1, 'months').endOf('month').format('YYYY-MM-DD')
          ]
        } else if (val === 'lastWeek') {
          this.dates = [
            moment().subtract(1, 'weeks').startOf('isoWeek').format('YYYY-MM-DD'),
            moment().subtract(1, 'weeks').endOf('isoWeek').format('YYYY-MM-DD')
          ]
        } else if (val === 'today') {
          this.dates = [
            moment().format('YYYY-MM-DD'),
            moment().format('YYYY-MM-DD')
          ]
        } else if (val === 'week') {
          this.dates = [
            moment().startOf('isoWeek').format('YYYY-MM-DD'),
            moment().endOf('isoWeek').format('YYYY-MM-DD')
          ]
        } else if (val === 'month') {
          this.dates = [
            moment().startOf('month').format('YYYY-MM-DD'),
            moment().endOf('month').format('YYYY-MM-DD')
          ]
        } else {
          this.dates = val
        }
      }
    }
  },
  mounted () {
    this.dateRange = this.rangeFor
    this.getReports(this.rangeFor)
  },
  methods: {
    getReports (mode) {
      this.loading = true
      for (const key in this.activeBtns) {
        this.activeBtns[key] = mode === key
      }
      if (mode) {
        this.dateRange = mode
      }
      this.fetchReports()
    },
    calculateTotalOf (items, key) {
      return items.reduce((prevVal, item) => {
        return +prevVal.toFixed(2) + +item[key].toFixed(2)
      }, 0).toFixed(2)
    },
    sortGroup (group, by) {
      if (group.sortBy !== by) {
        group.sortWay = null
        group.sortBy = by
      }
      if (group.sortWay === null) {
        group.sortWay = 'asc'
      } else if (group.sortWay === 'asc') {
        group.sortWay = 'desc'
      } else {
        group.sortWay = null
        group.sortBy = 'ID'
      }
      group.items = [...group.items.sort((a, b) => {
        return a[group.sortBy].toString().localeCompare(b[group.sortBy])
      })]
      if (group.sortWay === 'desc') {
        group.items.reverse()
      }
    },
    fetchReports () {
      this.axios.get(`/api/reportinglist/${this.dateRange[0]}/${this.dateRange[1]}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.reportsListRaw = res.data
        this.generateReportsList()
      }).catch(error => {
        this.loading = false
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    generateReportsList () {
      this.reportsList = []
      for (const item of this.reportsListRaw) {
        if (this.groupBy) {
          const groupIndex = this.reportsList.findIndex(g => g.group === item[this.groupBy])
          if (groupIndex > -1) {
            this.reportsList[groupIndex].items.push(item)
          } else {
            this.reportsList.push({
              group: item[this.groupBy],
              isOpen: false,
              items: [item]
            })
          }
        } else {
          if (this.reportsList.length) {
            this.reportsList[0].items.push(item)
          } else {
            this.reportsList.push({
              group: null,
              sortBy: null,
              sortWay: null,
              isOpen: true,
              items: [item]
            })
          }
        }
      }
      if (this.groupBy) {
        this.reportsList.sort((a, b) => {
          return a.group.toString().localeCompare(b.group.toString())
        })
      }
      this.loading = false
    }
  }
}
</script>
<style lang="scss">
  .group {
    margin: 1px 0;
  }
  .data-table {
    border-spacing: 0;
    border-collapse: collapse;
    min-width: 100%;
    font-weight: normal;
    color: var(--v-theme-on-surface);
    thead {
      position: sticky;
      z-index: 2;
      top: 0;
      background-color: var(--v-theme-on-surface);
      box-shadow: 0 1px 0px color-mix(in srgb, var(--v-customBorderColor-base), transparent 88%);
      user-select: none;
    }
    tr:last-child {
      td {
        border-bottom: none;
      }
    }
    tr:first-child {
      td {
        border-top: none;
      }
    }
    td {
      transition: border-color .15s ease-in-out;
      overflow: hidden;
      span {
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        text-overflow: -o-ellipsis-lastline;
      }
    }
    td, th {
      font-size: 14px;
      line-height: 21px;
      text-align: left;
      padding: 0 16px;
      min-height: 32px;
      border: 1px solid color-mix(in srgb, var(--v-customBorderColor-base), transparent 88%);
      &:first-child {
        border-left: none;
      }
      &:last-child {
        border-right: none;
      }
    }
    th {
      cursor: pointer;
      font-size: 12px;
      line-height: 18px;
      border-top: none;
      border-bottom: none;
      color: var(--v-theme-on-surface);
      white-space: nowrap;
      &.sorted-by {
        color: var(--v-theme-on-surface);
      }
    }
    tbody {
      tr {
        transition: background-color .15s ease-in-out;
        &:nth-child(even) {
          background-color: var(--v-theme-on-surface);
        }
        &:hover {
          background-color: rgba(0, 0, 0, .09);
          td {
            border-left-color: rgba(0, 0, 0, .09);
            border-right-color: rgba(0, 0, 0, .09);
          }
        }
      }
    }
  }
</style>
